import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useStripe } from '../../contexts/StripeContext';
import { auth } from '../../firebase';

const OnboardingStepStripe = ({ onNext, onBack }) => {
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stripeReady, setStripeReady] = useState(false);
  const { redirectToCheckout, getStripe } = useStripe();

  useEffect(() => {
    const checkStripeAvailability = () => {
      if (typeof window !== 'undefined' && window.Stripe) {
        setStripeReady(true);
      } else {
        setTimeout(checkStripeAvailability, 500);
      }
    };
    checkStripeAvailability();
  }, []);

  const handleContinue = async () => {
    if (!stripeReady) {
      setError('Payment system loading. Try again in a moment.');
      return;
    }

    sessionStorage.setItem('selectedPlan', selectedPlan);
    setError(null);
    setIsLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      const stripeInstance = getStripe();
      if (!stripeInstance) throw new Error('Payment system unavailable. Refresh and try again.');

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

      const customerResponse = await fetch(`${API_URL}/create-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, email: user.email }),
      });

      const customerData = await customerResponse.json();
      if (!customerResponse.ok) throw new Error(customerData.detail || 'Failed to set up billing');

      const priceId = selectedPlan === 'yearly' ? 'annually' : 'monthly';

      const response = await fetch(`${API_URL}/createCheckoutSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, priceId }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Checkout error');
      if (!data.sessionId) throw new Error('Missing checkout session');

      await redirectToCheckout(data.sessionId);
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-10">
        <h2 className="text-[1.75rem] sm:text-[2rem] font-light tracking-tight text-gray-900 mb-2">
          Choose your plan
        </h2>
        <p className="text-[15px] text-gray-400">
          Everything you need to run your business. Cancel anytime.
        </p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-3.5 bg-red-50 border border-red-100 rounded-xl"
        >
          <p className="text-[13px] text-red-600">{error}</p>
        </motion.div>
      )}

      <div className="space-y-3 mb-8">
        {/* Yearly */}
        <button
          onClick={() => setSelectedPlan('yearly')}
          className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
            selectedPlan === 'yearly'
              ? 'border-[#f97316] bg-[#f97316]/[0.03]'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2.5">
                <span className="text-[15px] font-medium text-gray-900">Yearly</span>
                <span className="text-[11px] tracking-wider uppercase font-medium text-[#f97316] bg-[#f97316]/10 px-2 py-0.5 rounded-full">
                  Save $60
                </span>
              </div>
              <p className="text-[13px] text-gray-400 mt-0.5">Billed annually</p>
            </div>
            <div className="text-right">
              <span className="text-[1.5rem] font-light text-gray-900">$20</span>
              <span className="text-[13px] text-gray-400">/mo</span>
            </div>
          </div>
        </button>

        {/* Monthly */}
        <button
          onClick={() => setSelectedPlan('monthly')}
          className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
            selectedPlan === 'monthly'
              ? 'border-[#f97316] bg-[#f97316]/[0.03]'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[15px] font-medium text-gray-900">Monthly</span>
              <p className="text-[13px] text-gray-400 mt-0.5">Cancel anytime</p>
            </div>
            <div className="text-right">
              <span className="text-[1.5rem] font-light text-gray-900">$25</span>
              <span className="text-[13px] text-gray-400">/mo</span>
            </div>
          </div>
        </button>
      </div>

      {/* What's included — minimal list */}
      <div className="mb-8 px-1">
        <p className="text-[12px] tracking-wider uppercase text-gray-400 mb-3">Includes</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {['AI assistant', 'Invoicing', 'Contracts', 'Client portal', 'Lead follow-ups', 'Scheduling'].map((feature) => (
            <span key={feature} className="text-[13px] text-gray-500">{feature}</span>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          onClick={onBack}
          className="text-[14px] text-gray-400 hover:text-gray-600 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleContinue}
          disabled={isLoading}
          className="px-8 py-3 bg-[#f97316] hover:bg-[#ea580c] text-white text-[14px] font-medium rounded-xl transition-colors disabled:opacity-50"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Processing
            </span>
          ) : (
            'Continue'
          )}
        </button>
      </div>
    </div>
  );
};

export default OnboardingStepStripe;
